import * as React from 'react';
import {
  Typography, Box, Button, Grid, Card, CardContent, Collapse,
  IconButton, InputAdornment, MenuItem, Chip,
} from '@mui/material';
import { connect } from 'react-redux';
import { FilterList, FilterListOff, Search } from '@mui/icons-material';
import { getBazaars, getVendors } from '../../app/store/actions/imsAdminActions';
import { centered_flex_box, main_button, MainInput } from '../../app/components/Styles';
import { BrandSelect } from '../../app/components/BrandSelect';
import BazaarsTable from '../../app/components/BazaarsTable';
import { BAZAAR_STATUSES, bazaarStatusLabel } from '../../app/components/bazaarUtils';

export const BazaarsDashboard = ({
  getBazaars, getVendors, bazaars, pendingCount, vendors, isLoading,
}) => {
  const [page, setPage] = React.useState(1);
  const [status, setStatus] = React.useState(null);
  const [fulfillmentMode, setFulfillmentMode] = React.useState(null);
  const [vendorId, setVendorId] = React.useState(null);
  const [search, setSearch] = React.useState('');
  const [showFilters, setShowFilters] = React.useState(false);
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  const buildParams = (pageNum, overrides = {}) => {
    const q = overrides.search !== undefined ? overrides.search : search;
    return {
      page: pageNum,
      limit: 100,
      status: (overrides.status !== undefined ? overrides.status : status) || undefined,
      fulfillmentMode: (overrides.fulfillmentMode !== undefined ? overrides.fulfillmentMode : fulfillmentMode) || undefined,
      vendorId: (overrides.vendorId !== undefined ? overrides.vendorId : vendorId) || undefined,
      search: q || undefined,
    };
  };

  React.useEffect(() => {
    if (isInitialLoad) {
      getBazaars(buildParams(page));
      getVendors({ page: 1, limit: 1000 });
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);

  React.useEffect(() => {
    if (!isInitialLoad) {
      getBazaars(buildParams(page));
    }
  }, [page]);

  React.useEffect(() => {
    if (!isInitialLoad) {
      setPage(1);
      getBazaars(buildParams(1));
    }
  }, [status, fulfillmentMode, vendorId]);

  const handleSearch = () => {
    setPage(1);
    getBazaars(buildParams(1));
  };

  const clearAllFilters = () => {
    setSearch('');
    setStatus(null);
    setFulfillmentMode(null);
    setVendorId(null);
    setPage(1);
    getBazaars({ page: 1, limit: 100 });
  };

  return (
    <div>
      <Box sx={{ width: '100%', padding: 5, mt: 2 }}>
        <Box sx={{ ...centered_flex_box, mb: 2, justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="h4" sx={{ color: 'var(--secColor)' }}>Bazaars</Typography>
            {(pendingCount || 0) > 0 && (
              <Chip
                label={`${pendingCount} pending`}
                color="warning"
                size="small"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Box>
          <Box>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              sx={{ ...main_button, my: 2, ml: 2 }}
            >
              {showFilters ? (
                <FilterListOff sx={{ color: 'var(--secondaryColor)' }} />
              ) : (
                <FilterList sx={{ color: 'var(--secColor4)' }} />
              )}
              Filters
            </Button>
          </Box>
        </Box>

        <Collapse in={showFilters}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', px: 5 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography>Search</Typography>
                    <MainInput
                      margin="normal"
                      fullWidth
                      placeholder="Search by bazaar name or location"
                      value={search}
                      onChange={({ target }) => setSearch(target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') handleSearch();
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleSearch}
                              edge="end"
                              sx={{ color: 'var(--secColor4)' }}
                            >
                              <Search />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography>Status</Typography>
                    <MainInput
                      fullWidth
                      value={status ?? ''}
                      onChange={({ target }) => setStatus(target.value || null)}
                      displayEmpty
                      select
                      sx={{ mt: 1 }}
                    >
                      <MenuItem value="">All statuses</MenuItem>
                      {BAZAAR_STATUSES.map((s) => (
                        <MenuItem key={s} value={s}>{bazaarStatusLabel(s)}</MenuItem>
                      ))}
                    </MainInput>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography>Fulfillment mode</Typography>
                    <MainInput
                      fullWidth
                      value={fulfillmentMode ?? ''}
                      onChange={({ target }) => setFulfillmentMode(target.value || null)}
                      displayEmpty
                      select
                      sx={{ mt: 1 }}
                    >
                      <MenuItem value="">All modes</MenuItem>
                      <MenuItem value="POS">Chainz POS</MenuItem>
                      <MenuItem value="Outbound">Outbound only</MenuItem>
                    </MainInput>
                  </Grid>
                  <Grid item xs={12}>
                    <BrandSelect
                      vendors={vendors}
                      value={vendorId}
                      label="Brand"
                      emptyLabel="All brands"
                      emptyValue={null}
                      fullWidth
                      margin="none"
                      sx={{ mt: 1 }}
                      onChange={(id) => setVendorId(id)}
                    />
                  </Grid>
                  <Grid item xs={2} sx={{ ...centered_flex_box }}>
                    <Button sx={{ ...main_button }} fullWidth onClick={clearAllFilters}>
                      Clear All
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Collapse>

        <BazaarsTable
          bazaars={bazaars?.docs}
          pagination={bazaars}
          page={page}
          setPage={setPage}
          isLoading={isLoading}
        />
      </Box>
    </div>
  );
};

const mapStateToProps = (state) => ({
  bazaars: state.records.bazaars,
  pendingCount: state.records.pendingCount,
  vendors: state.records.vendors,
  isLoading: state?.records?.isLoading || state?.wait?.isLoading,
});

export default connect(mapStateToProps, { getBazaars, getVendors })(BazaarsDashboard);
